import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

interface UseApiQueryOptions<T> {
  endpoint: string;
  enabled?: boolean;
}

interface ApiQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useApiQuery<T>({ endpoint, enabled = true }: UseApiQueryOptions<T>): ApiQueryResult<T> {
  const { get } = useApi();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await get(endpoint);
      setData(result.data as T);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load data';
      setError(msg);
      // eslint-disable-next-line no-console
      console.error(`useApiQuery error: ${endpoint}`, err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, get]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled, fetchData]);

  return { data, loading, error, refetch: fetchData };
}